import MiiaFwConfig from '@/configs/config'
import {
  GLOBAL_ERROR_CODE,
  type GlobalErrorCode,
  type SupportInfo
} from './entities/valueObjects/types'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { ERROR_MESSAGES } from '@/constants/error/errorMessage'

/**
 * @description 集約例外に関する機能をまとめたクラス
 */
export class GlobalError extends Error {
  private static isDisplayedDialog: boolean = false
  private errorCodes: string[]
  private readonly errorArgs: string[]
  private readonly supportInfo: SupportInfo
  private readonly replacementNotes: string[]

  /**
   * コンストラクタ
   * @param {string} errorCodes エラーコード、先頭にあるエラーコードがエラーメッセージとして出力される
   * @param {string[] | undefined} options.errorArgs エラーメッセージの文字引数
   * @param {SupportInfo | undefined} options.supportInfo カスタマーサポート情報
   * @param {string[] | undefined} options.replacementNotes 補足情報の置き換え文字の配列
   * @param {string | undefined} options.message 元のErrorクラスのメッセージ
   */
  constructor(
    errorCodes: string[],
    options?: {
      errorArgs?: string[]
      supportInfo?: SupportInfo
      replacementNotes?: string[]
      message?: string
    }
  ) {
    super(options?.message)
    this.errorCodes = errorCodes
    this.errorArgs = options?.errorArgs !== undefined ? options.errorArgs : []
    this.supportInfo = options?.supportInfo !== undefined ? options.supportInfo : defaultSupport
    this.replacementNotes = options?.replacementNotes === undefined ? [] : options.replacementNotes
  }

  /**
   * エラーメッセージを組み立てる
   * @param {GlobalErrorCode[]} globalErrorCodes エラーコードの配列
   * @return {string} エラーメッセージ
   */
  private makeErrorMessage(globalErrorCodes: string[]): string {
    // エラーコードが存在しない場合は共通メッセージの不明なエラーとして出す。メッセージは仮置き
    const errorInfo =
      ERROR_MESSAGES[globalErrorCodes[0]] !== undefined
        ? ERROR_MESSAGES[globalErrorCodes[0]]
        : ERROR_MESSAGES[GLOBAL_ERROR_CODE.unknownError]

    let message = `${errorInfo.message}\n\n`
    this.errorArgs.forEach((arg, index) => (message = message.replace(`{${index}}`, arg)))

    // CS問い合わせが必要な場合は、連絡先を追加する
    if (errorInfo.needsSupport !== undefined && errorInfo.needsSupport) {
      message += 'カスタマーサポート連絡先\n'
      message += `電話番号: ${this.supportInfo.phoneNumber}\n`
      message += `メールアドレス: ${this.supportInfo.email}\n\n`
    }

    // エラーコードを全て表示(空文字だったり、undefinedはfilterで除外)
    message += `エラーコード: ${globalErrorCodes.filter(code => code).join(', ')}`

    // CS問い合わせが必要な場合の補足情報、空や未定義の場合は表示しない
    if (
      errorInfo.needsSupport !== undefined &&
      errorInfo.needsSupport &&
      errorInfo.note !== undefined &&
      errorInfo.note !== ''
    ) {
      message += '\n\n補足情報\n'
      let note = errorInfo.note
      this.replacementNotes.forEach((replacementNote, index) => {
        const replacementString = `#replacement${index + 1}#`
        note = note.replace(replacementString, replacementNote)
      })
      message += `${note}\n`
    }

    return message
  }

  /**
   * エラーメッセージに記載されているタイトルを取得する。存在しない場合や空文字の場合はシステム名が出力される
   * @param {string} errorCode エラーコード
   * @returns {string} エラーダイアログのタイトル
   */
  private getDialogTitle(errorCode: string): string {
    const errorInfo = ERROR_MESSAGES[errorCode]
    return errorInfo.title === undefined || errorInfo.title === ''
      ? MiiaFwConfig.config.sysName
      : errorInfo.title
  }

  /**
   * エラーコードを末尾に追加する
   * @param {string} errorCode エラーコード
   * @return {GlobalError} 自分自身の参照
   */
  addErrorCodeLast(errorCode: string): GlobalError {
    this.errorCodes = this.errorCodes.concat(errorCode)
    return this
  }

  /**
   * エラーコードを先頭に追加する
   * @param {string} errorCode エラーコード
   * @return {GlobalError} 自分自身の参照
   */
  addErrorCodeFirst(errorCode: string): GlobalError {
    this.errorCodes = [errorCode, ...this.errorCodes]
    return this
  }

  /**
   * エラーメッセージを表示する
   * @param {} - 引数なし
   * @return 戻り値なし
   */
  showErrorMessage(): void {
    if (GlobalError.isDisplayedDialog) {
      // エラーダイアログは既に表示すれば処理しない
      return
    }
    GlobalError.isDisplayedDialog = true
    const commonModalStore = useCommonModalStore()
    commonModalStore
      .openAlertModal({
        title: this.getDialogTitle(this.errorCodes[0]),
        body: this.makeErrorMessage(this.errorCodes),
        okTitle: '確認'
      })
      .then(async result => {
        if (result === 'ok') {
          GlobalError.isDisplayedDialog = false
        }
      })
  }

  /**
   * 指定したエラーコードが含まれているか判定する
   * @param {GlobalErrorCode} code 判定対象のエラーコード
   * @returns {boolean} 含まれている場合はtrueを返す。
   */
  includes(code: GlobalErrorCode): boolean {
    return this.errorCodes.includes(code)
  }
}

// デフォルトの問い合わせ情報、DBにアクセスできなかったときなどに使用する
const defaultSupport: SupportInfo = {
  phoneNumber: '0120-000-000',
  email: 'xxx@test.com'
}
